import React, { Suspense, lazy, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import FallbackSpinner from 'components/FallbackSpinner';

import AuthContext from 'contexts/AuthContext';
import { SidebarProvider } from 'contexts/SidebarContext';
import {
  buildClientRouteTarget,
  clearRedirectTarget,
  getStoredRedirectTarget,
  isAuthFlowPath,
  syncRedirectTargetFromSearch,
} from 'shared/authRedirect';

const AuthRoutes = lazy(() => import('./AuthRoutes'));
const AppRoutes = lazy(() => import('./AppRoutes'));

const Routes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { authenticated, jwtManagement, user, validating } =
    useContext(AuthContext);

  useEffect(() => {
    window.scrollTo({ top: 0 });

    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthFlowPath(location.pathname)) {
      return;
    }

    syncRedirectTargetFromSearch(location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const canFinalizeAuthenticatedRedirect =
      authenticated &&
      !validating &&
      !!jwtManagement &&
      !!user.managed_organization;

    if (!canFinalizeAuthenticatedRedirect) {
      return;
    }

    const redirectTarget = getStoredRedirectTarget();
    const currentRouteTarget = buildClientRouteTarget(location);

    if (redirectTarget) {
      if (redirectTarget === currentRouteTarget) {
        clearRedirectTarget();
        return;
      }

      clearRedirectTarget();
      navigate(redirectTarget, { replace: true });
      return;
    }

    if (isAuthFlowPath(location.pathname)) {
      navigate('/', { replace: true });
    }
  }, [
    authenticated,
    jwtManagement,
    location,
    navigate,
    user.managed_organization,
    validating,
  ]);

  if (validating) {
    return <FallbackSpinner />;
  }

  return (
    <Suspense fallback={<FallbackSpinner />}>
      <SidebarProvider>
        {authenticated ? <AppRoutes /> : <AuthRoutes />}
      </SidebarProvider>
    </Suspense>
  );
};

export default Routes;
